/**
 * Step 2 link ranking → Step 3 scrape plan.
 *
 * The backend returns one manufacturer link and one vendor link with confidence
 * scores and an exact part number match (matched / not matched), plus the scrape
 * plan. Only matched links are scraped; no matched link → "No Match Found". Plan: scrape the manufacturer link first (no manufacturer
 * link → the vendor link), and also the vendor link only if the manufacturer
 * confidence is 90% or lower.
 */

const pct = (confidence) => (confidence === null || confidence === undefined) ? 'n/a' : `${confidence}%`;

/** "✅ exact part number E4324FG in URL" / "❌ no exact part number match" */
export const describeMatch = (match) => {
    if (!match) return '';
    if (match.matched) return `✅ exact part number "${match.partNumber}" in ${match.foundIn}`;
    if (match.partNumber && match.aiMatch === false) return `❌ AI says not an exact part number match`;
    return '❌ no exact part number match';
};

const roleLabel = (role) => (role === 'manufacturer' ? 'Manufacturer' : role === 'vendor' ? 'Vendor' : 'Link');

/** "manufacturer link (95%)" for log lines */
export const describePlanLink = (link) => link ? `${roleLabel(link.role).toLowerCase()} link (${pct(link.confidence)})` : '';

/**
 * Read the rank response into the values the pipeline uses, plus log lines.
 * Older responses without a scrapePlan fall back to scrapable_url.
 */
export function readRankResult(rankResp) {
    const manufacturer = rankResp.manufacturer || { name: '', url: rankResp.manufacturer_url || '', confidence: null };
    const vendor = rankResp.vendor || { name: '', url: rankResp.vendor_urls?.[0] || '', confidence: null };
    const plan = rankResp.scrapePlan || {
        primary: rankResp.scrapable_url ? { role: 'vendor', url: rankResp.scrapable_url, confidence: null } : null,
        secondary: null,
        skipped: null,
        rule: 'Scrape the selected link',
        minSecondaryConfidence: 90,
    };
    const notes = Array.isArray(rankResp.notes) ? rankResp.notes : [];
    const min = plan.minSecondaryConfidence ?? 90;

    const classifiedMfrs = (rankResp.manufacturers || []).map(m => ({ name: m.name || '', url: m.url || '', partNumber: m.partNumber || m.part_number || '', confidence: m.confidence ?? null, match: m.match || null }));
    const classifiedVendors = (rankResp.vendors || []).map(v => ({ name: v.name || '', url: v.url || '', partNumber: v.partNumber || v.part_number || '', confidence: v.confidence ?? null, match: v.match || null }));

    const logLines = [
        [manufacturer.url
            ? `  🏭 Manufacturer link: ${manufacturer.name || 'N/A'} — ${manufacturer.url} (confidence ${pct(manufacturer.confidence)}${manufacturer.match ? `, ${describeMatch(manufacturer.match)}` : ''})`
            : `  🏭 Manufacturer link: none found${manufacturer.name ? ` (brand: ${manufacturer.name})` : ''}`, manufacturer.url ? 'success' : 'detail'],
        [vendor.url
            ? `  🏪 Vendor link: ${vendor.name || 'N/A'} — ${vendor.url} (confidence ${pct(vendor.confidence)}${vendor.match ? `, ${describeMatch(vendor.match)}` : ''})`
            : '  🏪 Vendor link: none found', vendor.url ? 'success' : 'detail'],
        ...notes.map(note => [`  ℹ️ ${note}`, 'detail']),
    ];
    for (const link of plan.rejected || []) {
        logLines.push([`  🚫 ${roleLabel(link.role)} link not used: ${link.reason}`, 'warning']);
    }
    const noMatch = (rankResp.matchStatus || plan.matchStatus) === 'NO_MATCH_FOUND';
    if (noMatch) {
        logLines.push(['  ⛔ No Match Found — no link shows the exact part number; this row stops here', 'error']);
    }
    if (plan.primary) {
        logLines.push([`  📄 Scrape 1st: ${describePlanLink(plan.primary)}${plan.primary.role === 'vendor' ? ' — no manufacturer link' : ''}`, 'success']);
    }
    if (plan.secondary) {
        logLines.push([`  📄 Scrape 2nd: ${describePlanLink(plan.secondary)} — ${plan.secondary.reason || `manufacturer confidence not above ${min}%`}`, 'success']);
    } else if (plan.skipped) {
        logLines.push([`  ⏭ 2nd link not scraped: ${describePlanLink(plan.skipped)} — ${plan.skipped.reason}`, 'detail']);
    }

    const summary = noMatch
        ? 'No Match Found — no link shows the exact part number'
        : plan.primary
            ? `Manufacturer ${pct(manufacturer.confidence)} · Vendor ${pct(vendor.confidence)} → scrape ${plan.primary.role}${plan.secondary ? ` + ${plan.secondary.role}` : ' only'} (exact part number match)`
            : 'No link to scrape';

    return {
        manufacturer,
        vendor,
        plan,
        noMatch,
        notes,
        classifiedMfrs,
        classifiedVendors,
        bestUrl: plan.primary?.url || '',
        secondaryUrl: plan.secondary?.url || null,
        // The link not chosen for scraping — used only if the first link turns out to be a homepage
        fallbackUrl: plan.secondary?.url || plan.skipped?.url || null,
        dataSource: plan.primary?.role === 'manufacturer' ? 'Manufacturer URL' : 'Vendor URL',
        logLines,
        summary,
        logData: {
            manufacturer,
            vendor,
            scrapePlan: plan,
            matchStatus: rankResp.matchStatus || plan.matchStatus || null,
            notes,
            selectionMethod: rankResp.selectionMethod || 'AI_SELECTED',
        },
    };
}

/** Log label for a URL at scrape time, e.g. "manufacturer link (95%)" */
export function labelScrapeUrl(url, plan) {
    if (!url || !plan) return 'link';
    if (plan.primary?.url === url) return describePlanLink(plan.primary);
    if (plan.secondary?.url === url) return describePlanLink(plan.secondary);
    if (plan.skipped?.url === url) return `${describePlanLink(plan.skipped)}, fallback`;
    return 'manufacturer product page (from Step 2.5 validation)';
}
